import { captureException } from '@sentry/react'
import type { Middleware } from '@reduxjs/toolkit'
import { LOGOUT } from './authState'

type LogoutAction = {
  type: typeof LOGOUT
  reason?: string
}

/**
 * The dev-tools persona switch dispatches LOGOUT purely to drop the previous admin's
 * cached profile, and follows it with LOGIN_SUCCESS in the same microtask. Tearing down
 * storage there would purge the session it is in the middle of establishing.
 */
const NO_TEARDOWN_REASONS = ['devtools-persona-switch']

/**
 * `AuthValidate` clears the outgoing session and then authenticates the incoming one from
 * the SSO token in the URL. The previous admin's records must go, but a reload would
 * abandon the exchange that is already in flight.
 */
const NON_NAVIGATING_REASONS = ['session-replaced']

function isLogoutAction(action: unknown): action is LogoutAction {
  return typeof action === 'object' && action !== null && 'type' in action && action.type === LOGOUT
}

/**
 * Clears browser storage on every LOGOUT, whatever dispatched it.
 *
 * Admin funnels manual sign-out, the idle timeout, the mount inactivity check, SSO
 * session replacement, both restclient 401 paths and the settings-page sign-outs through
 * the one `LOGOUT` action — and `redux-state-sync` broadcasts it, so the other tabs tear
 * themselves down too. Hanging the teardown here rather than off each call site means the
 * next path someone adds is covered without being wired up.
 *
 * Runs after `next(action)` so the reducers have already reset: the flush inside the
 * teardown then writes post-logout state rather than re-persisting the patient list.
 */
export const logoutTeardownMiddleware: Middleware = () => (next) => (action) => {
  const result = next(action)

  if (isLogoutAction(action)) {
    const reason = action.reason ?? ''
    if (!NO_TEARDOWN_REASONS.includes(reason)) {
      // Imported lazily: this module is part of the store's own graph, and the teardown
      // reaches the persistor back out of it. The dynamic edge also keeps
      // `@segment/analytics-next` out of the entry bundle.
      // A chunk that fails to load (offline, blocked CDN, a stale deploy's hashed
      // filename) rejects here. Without this handler the teardown silently does not
      // happen *and* the rejection goes unobserved, so logout leaves persisted patient
      // records on disk with nothing in Sentry to say so. There is no fallback worth
      // attempting — the persistor lives behind this very import — so the honest
      // degradation is to report it loudly.
      void import('../utils/clearAllClientState')
        .then(({ clearAllClientState }) => clearAllClientState({ navigate: !NON_NAVIGATING_REASONS.includes(reason) }))
        .catch((error: unknown) => {
          console.error('logoutTeardownMiddleware: failed to run the logout teardown', error)
          captureException(error)
        })
    }
  }

  return result
}
